import useLocalStorage from './useLocalStorage';
import { UserMode } from '../contexts/ModeContext';
import type { MenuSelectOption } from '@equip.health/ui';
import localStorageConstants from '../constants/localStorage.constants';
import { OrganizeBy } from '../constants/task.constants';
import {
  loggedInEventTrackedKey,
  logInMethodKey,
  LogoutMethod,
  Path,
} from '~/routes/constants';

import { ApiCommand } from '../Api';
import urlConstants from '../constants/url.constants';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { useAuth0 } from '@auth0/auth0-react';
import { UserEvents } from '~/analytics/userEvents';
import { pageCount as pageCountOptions } from '~/lib/constants/list.constants';
import useApi from './useApi';

const useHandleLogout = () => {
  const { trackEvent } = useAnalytics();
  const { logout } = useAuth0();
  const { resetAnalytics } = useAnalytics();
  const { sendRequest: revokeRefreshToken } = useApi();

  const { setValue: setMode } = useLocalStorage<UserMode>(
    'userMode',
    UserMode.UNKNOWN,
  );
  const { removeValue: removeUserPageCount } =
    useLocalStorage<MenuSelectOption>(
      localStorageConstants.userPageCount,
      pageCountOptions[0],
    );
  const { removeValue: removeTaskFilter } =
    useLocalStorage<TaskFiltersInLocalStorage>(
      localStorageConstants.taskFilter,
      {},
    );
  const { removeValue: removeTaskOrganizeBy } = useLocalStorage<OrganizeBy>(
    localStorageConstants.taskOrganizeBy,
    OrganizeBy.DUE_DATE,
  );
  const { removeValue: removeIntent } = useLocalStorage<string>(
    'intent',
    Path.HOME,
  );
  // ponytail: removed directly rather than via useLocalStorage, which would
  // seed the key with an initial value derived from the current user and pull
  // GlobalContext into a cycle with this hook.
  const removePatientFilter = () =>
    localStorage.removeItem(localStorageConstants.patientFilter);
  const { removeValue: removeChannelUrl } = useLocalStorage<string>(
    localStorageConstants.channelUrl,
    null,
  );
  const { removeValue: removeLoginMethodKey } = useLocalStorage<string>(
    logInMethodKey,
    null,
  );

  const revokeRefreshTokenOnLogout = () => {
    revokeRefreshToken({
      callback() {
        logout({ logoutParams: { returnTo: window.location.origin } });
      },
      command: ApiCommand.PUT,
      url: urlConstants.users.logout,
    });
  };

  const handleTrackEvent = (didUserInitiateLogout: boolean) => {
    trackEvent(UserEvents.loggedOut, {
      method: didUserInitiateLogout
        ? LogoutMethod.MANUAL_LOGOUT
        : LogoutMethod.IDLE_LOGOUT,
    });
  };

  const handleLogout = (didUserInitiateLogout = false) => {
    revokeRefreshTokenOnLogout();
    setMode(UserMode.UNKNOWN);
    removeChannelUrl();
    removeIntent();
    if (didUserInitiateLogout) {
      removePatientFilter();
      removeTaskOrganizeBy();
      removeTaskFilter();
      removeUserPageCount();
    }
    removeLoginMethodKey();
    sessionStorage.removeItem(loggedInEventTrackedKey);
    handleTrackEvent(didUserInitiateLogout);
    resetAnalytics();
  };

  return { logout: handleLogout };
};

export default useHandleLogout;
